import React from "react";
import { Reveal } from "@/components/Reveal";
import { Instagram, Signal, Wifi, BatteryFull } from "lucide-react";

const IG_URL = "https://www.instagram.com/houseofappetite.dg";

const PhoneMock = () => (
  <a
    href={IG_URL}
    target="_blank"
    rel="noopener noreferrer"
    data-testid="feed-phone-link"
    aria-label="Open @houseofappetite.dg on Instagram"
    className="group relative block w-[280px] sm:w-[320px] mx-auto"
  >
    <div className="absolute -inset-12 rounded-full bg-[radial-gradient(closest-side,rgba(179,145,74,0.3),transparent_72%)] blur-2xl pointer-events-none" />
    <div className="relative transition-transform duration-500 ease-out lg:rotate-[-3deg] lg:group-hover:rotate-0 group-hover:-translate-y-1.5">
      <div className="absolute -left-[3px] top-[104px] w-[3px] h-8 rounded-l-md bg-[#2e2f31]" />
      <div className="absolute -left-[3px] top-[150px] w-[3px] h-12 rounded-l-md bg-[#2e2f31]" />
      <div className="absolute -right-[3px] top-[136px] w-[3px] h-16 rounded-r-md bg-[#2e2f31]" />
      <div className="rounded-[52px] p-[3px] bg-gradient-to-b from-[#55575a] via-[#232526] to-[#3d3f42] shadow-[0_45px_90px_-25px_rgba(0,0,0,0.7)]">
        <div className="rounded-[49px] bg-black p-[9px]">
          <div className="relative rounded-[40px] overflow-hidden bg-[#fdfbf6] h-[580px] sm:h-[640px] flex flex-col">
            <div className="absolute top-[9px] left-1/2 -translate-x-1/2 w-[86px] h-[24px] bg-black rounded-full z-30 flex items-center justify-end pr-[9px]">
              <span className="w-[7px] h-[7px] rounded-full bg-[#17242f]" />
            </div>
            <div className="flex items-center justify-between px-7 pt-[13px] pb-1 shrink-0 text-[#0b0c0d]">
              <span className="text-[12px] font-semibold tracking-tight">9:41</span>
              <span className="flex items-center gap-[5px]">
                <Signal size={12} strokeWidth={2.5} />
                <Wifi size={13} strokeWidth={2.5} />
                <BatteryFull size={16} strokeWidth={2} />
              </span>
            </div>
            <div className="flex items-center gap-2.5 px-4 py-2.5 border-b border-black/[0.07] shrink-0">
              <span className="w-8 h-8 rounded-full bg-[#0E3B2E] border border-[rgba(179,145,74,0.75)] flex items-center justify-center shrink-0">
                <span className="font-display text-[#F4EFE4] text-[13px] leading-none pb-[2px]">a°</span>
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[12px] font-semibold text-[#0b0c0d] leading-tight truncate">houseofappetite.dg</span>
                <span className="block text-[10px] text-black/45 leading-tight">house of appetite · Chennai</span>
              </span>
              <span className="text-[11px] font-semibold text-white bg-[#0095F6] rounded-lg px-3 py-[6px]">Follow</span>
            </div>
            <div className="relative flex-1 overflow-hidden">
              <div className="hoa-ig-scroll">
                <img src="/images/ig-feed.webp" fetchPriority="high" decoding="async" alt="Instagram feed of House of Appetite bakes" className="w-full block" />
                <img src="/images/ig-feed.webp" alt="" aria-hidden="true" className="w-full block" />
              </div>
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-[#fdfbf6] to-transparent" />
            </div>
            <div className="shrink-0 h-[20px] flex items-center justify-center">
              <span className="w-[96px] h-[4px] rounded-full bg-black/80" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </a>
);

/* ------------------------------------------------------------------ 06 */
export const TheFeed = () => (
  <section data-testid="feed-section" className="hoa-texture hoa-grain">
    <div className="relative z-[1] mx-auto w-full max-w-[1120px] px-5 sm:px-6 py-20 sm:py-28">
      <Reveal>
        <div className="flex items-center gap-4 mb-8 sm:mb-10">
          <span className="font-label text-[10px] sm:text-[11px] tracking-[0.3em] uppercase hoa-gold-text">
            06 — The Feed
          </span>
          <span className="flex-1 h-px bg-[rgba(179,145,74,0.35)]" />
        </div>
      </Reveal>
      <div className="grid lg:grid-cols-2 gap-14 lg:gap-16 items-center">
        <div>
          <Reveal>
            <h2 className="font-display text-[var(--hoa-cream-50)] text-4xl sm:text-5xl leading-[1.05]">
              Fresh from the oven,
              <br />
              <span className="hoa-gold-text">straight to your feed.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="font-body text-[rgba(238,233,222,0.72)] text-base sm:text-lg leading-[1.85] mt-7 max-w-xl">
              Every bake, every drop, every “coming soon” lands on Instagram first. Follow along
              for live menus, fresh-out-the-oven moments and quiet peeks inside the kitchen.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <a
              href={IG_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="feed-follow-btn"
              className="inline-flex items-center gap-2.5 mt-9 rounded-full bg-[var(--hoa-cream-50)] text-[#0b0c0d] font-label text-[11px] tracking-[0.22em] uppercase px-7 py-4 transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-12px_rgba(179,145,74,0.5)]"
            >
              <Instagram size={15} strokeWidth={2} />
              Follow @houseofappetite.dg
            </a>
          </Reveal>
          <Reveal delay={0.28}>
            <p className="font-display italic hoa-gold-text text-xl mt-8">give in.</p>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <PhoneMock />
        </Reveal>
      </div>
    </div>
  </section>
);
